import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeb3 } from '../../context/Web3Context';
import { useToast } from '../../context/ToastContext';
import DashboardLoader from '../Loading/DashboardLoader';
import { AnimVal, MCard, PageWrap, Confetti, FutureStakingTimer, FutureStakingGauge } from '../UI';
import CONTRACTS from '../../contracts/addresses';
import IncomeABI from '../../contracts/IncomeABI.json';
import PoolsABI from '../../contracts/PoolsABI.json';
import { formatUSDT } from '../../utils/helpers';

const RANKS = [
  { name: '1 Star Leader', icon: '⭐', threshold: 100 },
  { name: '2 Star Leader', icon: '⭐⭐', threshold: 500 },
  { name: '3 Star Leader', icon: '⭐⭐⭐', threshold: 1000 },
  { name: '4 Star Elite', icon: '🌟', threshold: 5000 },
  { name: '5 Star Elite', icon: '🌟🌟', threshold: 10000 },
  { name: '6 Star Champion', icon: '🌟🌟🌟', threshold: 50000 },
  { name: '7 Star Champion', icon: '💎', threshold: 100000 },
  { name: 'Super Star', icon: '💎💎', threshold: 1000000 },
  { name: 'Universal Star', icon: '👑', threshold: 10000000 }
];

export default function IncomePage() {
  const { contracts, userId, signer } = useWeb3();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  const [pendingROI, setPendingROI] = useState(ethers.BigNumber.from(0));
  const [rank, setRank] = useState(0);
  const [rankName, setRankName] = useState('');
  const [monthlyBinary, setMonthlyBinary] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [wAmt, setWAmt] = useState('');
  const [wRaw, setWRaw] = useState(null);
  const [wOpen, setWOpen] = useState(false);
  const [wLoading, setWLoading] = useState(false);
  const [fsInfo, setFsInfo] = useState(null);
  const [timeUnit, setTimeUnit] = useState(600);
  const [claimingFS, setClaimingFS] = useState(false);
  const [fsReady, setFsReady] = useState(false);
  const [royaltyClaimable, setRoyaltyClaimable] = useState(ethers.BigNumber.from(0));
  const [royaltyRounds, setRoyaltyRounds] = useState(0);
  const [claimingRoyalty, setClaimingRoyalty] = useState(false);
  const [royalPoolEligible, setRoyalPoolEligible] = useState(false);

  useEffect(() => {
    if (!contracts.storage || !userId) return;
    (async () => {
      try {
        const [inc, avail, tu] = await Promise.all([
          contracts.storage.getIncomeInfo(userId),
          contracts.storage.getAvailableBalance(userId),
          contracts.storage.TIME_UNIT()
        ]);
        setTimeUnit(tu.toNumber());
        let pr = ethers.BigNumber.from(0);
        try { const ic = new ethers.Contract(CONTRACTS.INCOME, IncomeABI, signer); pr = await ic.getPendingROI(userId); } catch {}
        let rk = 0, rn = '', mb = 0;
        const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer);
        try {
          const r = await pc.getUserRank(userId);
          rk = r[0].toNumber(); rn = r[1];
          const mbi = await pc.getMonthlyBinaryIncome(userId);
          mb = parseFloat(ethers.utils.formatUnits(mbi, 18));
        } catch {}
        // Future staking info
        let fsi = null;
        try {
          const fs = await pc.getFutureStakingInfo(userId);
          fsi = { accumulated: fs[0], cycleStart: fs[1].toNumber(), totalEarned: fs[2], totalAccumulated: fs[3], capLimit: fs[4], daysUntilPayout: fs[5].toNumber() };
        } catch {}
        // Royalty claimable
        let rc = ethers.BigNumber.from(0), rr = 0;
        try {
          const res = await pc.getRoyaltyClaimable(userId);
          rc = res[0]; rr = res[1].toNumber();
        } catch {}
        setD({
          roi: inc[0], binary: inc[1], referral: inc[2], level: inc[3], leadership: inc[4],
          mentorship: inc[5], futureStk: inc[6], royalty: inc[7], royalPool: inc[8],
          tokenReward: inc[9], withdrawn: inc[10], available: avail
        });
        setPendingROI(pr); setRank(rk); setRankName(rn); setMonthlyBinary(mb);
        setFsInfo(fsi);
        setRoyaltyClaimable(rc); setRoyaltyRounds(rr);
        // V6: Royal pool eligibility
        let rpE = false;
        try { rpE = await pc.isRoyalPoolEligible(userId); } catch {}
        setRoyalPoolEligible(rpE);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [contracts.storage, userId, signer]);

  const claimROI = async () => {
    setClaiming(true);
    try { const ic = new ethers.Contract(CONTRACTS.INCOME, IncomeABI, signer); toast.info('Claiming...'); const tx = await ic.processROI(userId); await tx.wait(); toast.success('Claimed! 🎉'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Failed'); }
    setClaiming(false);
  };

  const claimFutureStaking = async () => {
    setClaimingFS(true);
    try { const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer); toast.info('Claiming future staking Salary...'); const tx = await pc.processFutureStaking(userId); await tx.wait(); toast.success('Future staking Salary claimed! 🎉'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Failed'); }
    setClaimingFS(false);
  };

  const claimRoyalty = async () => {
    setClaimingRoyalty(true);
    try { const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer); toast.info('Claiming royalty...'); const tx = await pc.claimRoyaltyIncome(); await tx.wait(); toast.success('Royalty claimed! 🎉'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Failed'); }
    setClaimingRoyalty(false);
  };

  const handleW = async () => {
    let sendAmt;
    if (wRaw) { sendAmt = wRaw; }
    else { if (!wAmt || parseFloat(wAmt) <= 0) { toast.error('Enter amount'); return; } sendAmt = ethers.utils.parseUnits(wAmt, 18); }
    setWLoading(true);
    try { const tx = await contracts.logic.withdraw(sendAmt); await tx.wait(); toast.success('Withdrawn!'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Failed'); }
    setWLoading(false);
  };

  const setMaxW = () => { if (d?.available) { setWRaw(d.available); setWAmt(ethers.utils.formatUnits(d.available, 18)); } };

  if (loading) return <DashboardLoader />;
  if (!d) return null;
  const total = [d.roi, d.binary, d.referral, d.level, d.leadership, d.mentorship, d.futureStk, d.royalty, d.royalPool].reduce((a, b) => a.add(b), ethers.BigNumber.from(0));
  const tokenRewardFloat = (() => { try { return parseFloat(ethers.utils.formatUnits(d.tokenReward, 18)); } catch { return 0; } })();

  const incomes = [
    { l: 'ROI Income', v: d.roi, i: '📈', c: 'var(--cyan)', cls: 'bounce-icon' },
    { l: 'Binary', v: d.binary, i: '⚡', c: 'var(--gold)', cls: 'pulse-icon' },
    { l: 'Referral', v: d.referral, i: '🤝', c: 'var(--green)' },
    { l: 'Level', v: d.level, i: '📊', c: 'var(--purple)' },
    { l: 'Direct ROI', v: d.leadership, i: '👑', c: 'var(--gold)', cls: 'pulse-icon' },
    { l: 'Mentorship', v: d.mentorship, i: '🎓', c: 'var(--cyan)' },
    { l: 'Royalty Pool', v: d.royalty, i: '💎', c: 'var(--gold)', cls: 'bounce-icon' },
    ...(royalPoolEligible ? [{ l: 'Royal Pool', v: d.royalPool, i: '🏛️', c: 'var(--cyan)' }] : [])
  ];

  const nextRankIdx = RANKS.findIndex(r => monthlyBinary < r.threshold);
  const hasFSCycle = fsInfo && fsInfo.cycleStart > 0 && fsInfo.accumulated.gt(0);

  return (<PageWrap>
    <Confetti active={confetti} />

    {/* === AVAILABLE BALANCE === */}
    <MCard className="hero-card" delay={0} style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: 8 }}>Available Balance</div>
      <AnimVal value={d.available} className="val val-hero val-cyan" />
      <div className="g2" style={{ marginTop: 16 }}>
        <div className="fcc"><AnimVal value={total} className="val val-md val-green" /><div className="label">Total Earned</div></div>
        <div className="fcc"><AnimVal value={d.withdrawn} className="val val-md" color="var(--text2)" /><div className="label">Withdrawn</div></div>
      </div>
    </MCard>

    {/* === PENDING ROI === */}
    <MCard className="conic-card" delay={0.08} style={{ textAlign: 'center', padding: '28px 22px' }}>
      <div className="roi-label">Pending ROI</div>
      <motion.div className="roi-val" initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}><span>$</span>{formatUSDT(pendingROI)}</motion.div>
      <motion.button className="btn btn-primary btn-full" onClick={claimROI} disabled={claiming} style={{ padding: 14, marginTop: 12 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>{claiming ? '⏳...' : '📈 Claim ROI'}</motion.button>
    </MCard>

    {/* === ROYALTY CLAIM === */}
    {royaltyRounds > 0 && royaltyClaimable.gt(0) && (
      <MCard className="conic-card" delay={0.12} style={{ textAlign: 'center', padding: '22px' }}>
        <div style={{ fontSize: '0.6rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 6 }}>Royalty Income Available</div>
        <motion.div style={{ fontFamily: 'var(--font-h)', fontSize: '1.8rem', fontWeight: 900, color: 'var(--gold)' }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.3 }}>${formatUSDT(royaltyClaimable)}</motion.div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text2)', margin: '8px 0 12px' }}>{royaltyRounds} unclaimed round{royaltyRounds > 1 ? 's' : ''}</div>
        <motion.button className="btn btn-gold btn-full" onClick={claimRoyalty} disabled={claimingRoyalty} style={{ padding: 14 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>{claimingRoyalty ? '⏳ Claiming...' : '💎 Claim Royalty'}</motion.button>
      </MCard>
    )}

    {/* === RANK === */}
    {rank > 0 && <MCard delay={0.16}>
      <div className="fb">
        <div><div className="label">Your Rank</div><div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4 }}>{RANKS[rank - 1]?.icon} {rankName}</div></div>
        <span className="badge badge-g">{rankName}</span>
      </div>
    </MCard>}

    {/* === INCOME STREAMS === */}
    <div className="stitle">Income Streams</div>
    <div className="g2">
      {incomes.map((x, i) => (
        <MCard key={i} className="icard" delay={0.2 + i * 0.04} style={{ margin: 0 }}>
          <div className={`anim-icon ${x.cls || ''}`} style={{ fontSize: '1.15rem', marginBottom: 5 }}>{x.i}</div>
          <div className="icard-label">{x.l}</div>
          <AnimVal value={x.v} className="icard-val" color={x.c} />
        </MCard>
      ))}
      {/* Token Reward */}
      <MCard className="icard" delay={0.58} style={{ margin: 0 }}>
        <div className="anim-icon pulse-icon" style={{ fontSize: '1.15rem', marginBottom: 5 }}>🪙</div>
        <div className="icard-label">Token Reward</div>
        <span className="icard-val" style={{ color: 'var(--green)' }}>{tokenRewardFloat.toFixed(2)} RF</span>
      </MCard>
    </div>

    {/* === FUTURE STAKING - FULL DESIGN === */}
    <div className="stitle">Future Staking Salary</div>
    <MCard delay={0.6} className="card fs-card">
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: '0.6rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 4 }}>5% of Binary Match</div>
        <div style={{ fontSize: '0.52rem', color: 'var(--purple)' }}>2% ROI every 30 days • 2x Cap</div>
      </div>

      {/* Speedometer Gauge */}
      {fsInfo && (
        <FutureStakingGauge earned={fsInfo.totalEarned} cap={fsInfo.capLimit} />
      )}

      {/* Stats Grid */}
      <div className="g3" style={{ marginTop: 8 }}>
        <div className="fcc">
          <AnimVal value={fsInfo?.accumulated || 0} className="val val-sm val-purple" />
          <div className="label">Accumulated</div>
        </div>
        <div className="fcc">
          <AnimVal value={fsInfo?.totalEarned || d.futureStk} className="val val-sm val-green" />
          <div className="label">Total Earned</div>
        </div>
        <div className="fcc">
          <AnimVal value={fsInfo?.capLimit || 0} className="val val-sm val-gold" />
          <div className="label">Cap (2x)</div>
        </div>
      </div>

      {/* Remaining */}
      {fsInfo && fsInfo.capLimit.gt(0) && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <span style={{ fontSize: '0.68rem', color: 'var(--text2)' }}>Remaining: </span>
          <AnimVal value={fsInfo.capLimit.sub(fsInfo.totalEarned.gt(fsInfo.capLimit) ? fsInfo.capLimit : fsInfo.totalEarned)} className="val val-sm val-cyan" />
        </div>
      )}

      {/* 30-Day Timer with Pending Count */}
      {hasFSCycle && (
        <div style={{ marginTop: 16, padding: '16px', background: 'rgba(123,97,255,0.05)', borderRadius: 14, border: '1px solid rgba(123,97,255,0.12)' }}>
          <FutureStakingTimer cycleStart={fsInfo.cycleStart} timeUnit={timeUnit} cycleDays={30} onReady={setFsReady} />
        </div>
      )}

      {/* Claim Button */}
      {hasFSCycle && fsReady && (
        <motion.button className="btn btn-gold btn-full" onClick={claimFutureStaking} disabled={claimingFS}
          style={{ padding: 16, marginTop: 16 }}
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
          {claimingFS ? '⏳ Claiming...' : `🔓 Claim Future Staking Salary ($${formatUSDT(fsInfo.accumulated)})`}
        </motion.button>
      )}

      {/* No cycle yet */}
      {!hasFSCycle && (
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: '0.72rem', color: 'var(--text3)' }}>
          Earn binary income to start accumulating future staking Salary
        </div>
      )}
    </MCard>

    {/* === WITHDRAW === */}
    <div className="stitle">Withdraw</div>
    <MCard delay={0.65}>
      <div className="fb" style={{ marginBottom: 14 }}>
        <span style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>Available</span>
        <AnimVal value={d.available} className="val val-md val-cyan" />
      </div>
      <AnimatePresence>
        {wOpen ? (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
            <div style={{ position: 'relative' }}>
              <input type="text" placeholder="Amount" value={wAmt} onChange={e => { setWAmt(e.target.value); setWRaw(null); }} style={{ paddingRight: 60 }} />
              <motion.button onClick={setMaxW} whileTap={{ scale: 0.9 }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,245,255,0.15)', border: '1px solid rgba(0,245,255,0.3)', borderRadius: 8, padding: '4px 12px', color: 'var(--cyan)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>MAX</motion.button>
            </div>
            <div className="wd-note">10% deduction • 5% RF token reward</div>
            <div className="fr">
              <motion.button className="btn btn-primary btn-full" onClick={handleW} disabled={wLoading} whileTap={{ scale: 0.97 }}>{wLoading ? '⏳...' : '💸 Withdraw'}</motion.button>
              <motion.button className="btn btn-ghost" onClick={() => { setWOpen(false); setWRaw(null); }} style={{ minWidth: 80 }} whileTap={{ scale: 0.95 }}>Cancel</motion.button>
            </div>
          </motion.div>
        ) : <motion.button className="btn btn-primary btn-full" onClick={() => setWOpen(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>💸 Withdraw</motion.button>}
      </AnimatePresence>
    </MCard>

    {/* === RANK ELIGIBILITY === */}
    <div className="stitle">Rank Eligibility</div>
    <MCard delay={0.75}>
      <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginBottom: 14 }}>
        Monthly Binary: <span style={{ color: 'var(--gold)', fontWeight: 700, fontFamily: 'var(--font-m)' }}>${monthlyBinary.toFixed(2)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {RANKS.map((r, i) => {
          const pct = Math.min((monthlyBinary / r.threshold) * 100, 100);
          const achieved = rank > i;
          const isNext = i === nextRankIdx;
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + i * 0.05 }}
              style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${achieved ? 'rgba(0,230,118,0.2)' : isNext ? 'rgba(0,245,255,0.15)' : 'rgba(255,255,255,0.04)'}`, background: achieved ? 'rgba(0,230,118,0.04)' : isNext ? 'rgba(0,245,255,0.03)' : 'transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.9rem' }}>{r.icon}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: achieved ? 'var(--green)' : isNext ? '#fff' : 'var(--text2)' }}>{r.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-m)' }}>${r.threshold.toLocaleString()}</span>
                  {achieved && <span style={{ fontSize: '0.6rem', color: 'var(--green)', fontWeight: 700 }}>✓</span>}
                </div>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                  transition={{ duration: 1, delay: 0.9 + i * 0.05, ease: 'easeOut' }}
                  style={{ height: '100%', borderRadius: 2, background: achieved ? 'var(--green)' : pct > 50 ? 'var(--gold)' : 'var(--cyan)' }} />
              </div>
              {isNext && !achieved && (
                <div style={{ fontSize: '0.55rem', color: 'var(--text3)', marginTop: 4 }}>
                  ${monthlyBinary.toFixed(0)} / ${r.threshold.toLocaleString()} ({pct.toFixed(1)}%)
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </MCard>
  </PageWrap>);
}
